// The share-dialog state machine, subject-agnostic and framework-free so `test/share-flow.test.ts`
// can walk every path (type → submit → question → list → remove) with no DOM.
//
// It is generic over WHAT is being shared because the logic is not about apps or files: it is about
// an email that may be a typo, a server that may answer "I wrote nothing, ask them", and a list that
// may have failed to load. Every one of those rules was learned the hard way and none of them is
// subject-specific, so they live in one place rather than in two copies that drift and where only
// one gets the next fix.
//
// Both subjects run on it: `app-share-flow.ts` and `file-share-flow.ts` are now nothing but their
// own `ShareCopy`. There is no second copy of these rules to keep in step.
//
// The one rule the whole thing turns on: **there is no visibility field.** A subject is private
// exactly when its share list came back EMPTY and the read SUCCEEDED. So the outcomes below are
// three, not two — a list that failed to load is NOT private and must never be shown as such.
// Telling someone their file is private when we simply could not find out is the worst thing this
// dialog can say.

/** What the dialog holds while it is open. Everything else it renders comes from the list state. */
export interface ShareDialogState {
  email: string;
  /** A share POST is in flight. */
  submitting: boolean;
  /** The grantee whose revoke is in flight, so only that row's Remove goes quiet. */
  removing: string | null;
  error: ShareError | null;
  /**
   * The address just shared with, when nobody answered to it.
   *
   * Held only until the next action. It is a thing to NOTICE — "you may have mistyped that" — not a
   * state the dialog sits in, and not an error: the share worked and the access is waiting.
   */
  invitedEmail: string | null;
  /**
   * The address the SERVER asked about, meaning NOTHING WAS WRITTEN.
   *
   * Set when the address is neither a known Symphony user nor one of the owner's contacts — which is
   * exactly what a typo looks like. The dialog must render a question and re-submit with
   * `confirmUnknown` if the owner says yes. Rendering this as success is the single worst bug
   * available here: the owner believes they shared, and nobody ever will have.
   */
  confirmEmail: string | null;
  /** The grantee could not be told. The grant is real; only the message failed. */
  notifyFailed: boolean;
  /** A link to hand somebody who has no Symphony account yet. */
  shareLink: string | null;
}

export type ShareError = 'invalidEmail' | 'shareFailed' | 'removeFailed';

export const initialShareDialogState: ShareDialogState = {
  email: '',
  submitting: false,
  removing: null,
  error: null,
  invitedEmail: null,
  confirmEmail: null,
  notifyFailed: false,
  shareLink: null,
};

/**
 * The i18n keys one subject's dialog uses. Passing them in is what makes this module generic without
 * it ever inventing copy of its own — a caller that forgets a key gets a type error, not a blank.
 */
export interface ShareCopy {
  invalidEmail: string;
  shareFailed: string;
  removeFailed: string;
  private: string;
  privateHint: string;
  sharedWithOne: string;
  sharedWithOther: string;
  loadFailed: string;
  retry: string;
  unnamed: string;
}

/** The catalog key for each failure. Kept here so the dialog never invents copy of its own. */
export function shareErrorKey(error: ShareError, copy: ShareCopy): string {
  switch (error) {
    case 'invalidEmail':
      return copy.invalidEmail;
    case 'shareFailed':
      return copy.shareFailed;
    case 'removeFailed':
      return copy.removeFailed;
  }
}

/** The list state this machine reads. Structural, so any subject's data layer satisfies it. */
export type ShareListState<Row> = { status: 'loading' } | { status: 'ready'; shares: Row[] } | { status: 'error' };

/**
 * The honest line under the people list — the three-way answer this exists to keep distinct.
 *
 * `null` while the list is still loading: saying nothing is the only correct thing to say before the
 * read answers. `retryKey` is present on exactly one branch, because a retry only makes sense for a
 * read that failed; offering one under "Private" would imply the empty list was in doubt.
 */
export type AccessLine =
  | { kind: 'private'; key: string; hintKey: string }
  | { kind: 'shared'; key: string; params?: { count: number } }
  | { kind: 'loadFailed'; key: string; retryKey: string };

export function accessLine<Row>(shares: ShareListState<Row>, copy: ShareCopy): AccessLine | null {
  if (shares.status === 'loading') return null;
  // An error is NOT an empty list. This branch must come before any length check — reading `.shares`
  // off a failed state is exactly how a failed read turns into a "Private" badge.
  if (shares.status === 'error') return { kind: 'loadFailed', key: copy.loadFailed, retryKey: copy.retry };
  const count = shares.shares.length;
  if (count === 0) return { kind: 'private', key: copy.private, hintKey: copy.privateHint };
  if (count === 1) return { kind: 'shared', key: copy.sharedWithOne };
  return { kind: 'shared', key: copy.sharedWithOther, params: { count } };
}

/**
 * A grantee's label. Name and email are both OPTIONAL on a share — the row still exists, still holds
 * a real grant, and must still be listed and revocable. When neither is there the caller resolves
 * the `unnamed` key rather than rendering a raw recipient id at a person.
 */
export function shareLabel(
  share: { granteeName?: string; granteeEmail?: string },
  copy: ShareCopy,
): { text: string } | { key: string } {
  const text = (share.granteeName || share.granteeEmail || '').trim();
  return text ? { text } : { key: copy.unnamed };
}

/**
 * Mirrors the server's `isEmailShaped`. The client copy exists only so a typo is caught before a
 * round trip — the SERVER's copy is the one that matters, because grantee resolution is
 * get-or-create and a bad address mints a recipient.
 */
export function isEmailShaped(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export interface ShareFlowDeps {
  /**
   * Rejects on failure — a share that did not land must never look like one that did.
   *
   * `granteeIsNew` means the share CREATED the person: nobody answered to that address until now.
   * Not a failure — the access is real and waiting — but the only moment a typo is observable, since
   * nothing about that share will ever fail afterwards.
   */
  share: (
    subjectId: string,
    email: string,
    confirmUnknown: boolean,
  ) => Promise<{
    granteeIsNew?: boolean;
    requiresConfirmation?: boolean;
    granteeNotified?: boolean;
    /**
     * The grant ALREADY EXISTED, so this call announced nothing on purpose.
     *
     * Without it `granteeNotified === false` means two opposite things — we tried to tell them and
     * could not, and we deliberately did not because they were told when the grant was made — and
     * the dialog sends the owner off to inform somebody who was messaged fine.
     */
    alreadyShared?: boolean;
    shareLink?: string | null;
  } | void>;
  revoke: (subjectId: string, granteeRecipientId: string) => Promise<void>;
  setState: (state: ShareDialogState) => void;
  /** Re-read the share list after a change, so the people list is the server's answer and not ours. */
  reload: () => void;
}

export type ShareAttempt = 'shared' | 'question' | 'failed';

/**
 * Submit pressed (or Enter in the field). A malformed address never reaches the network.
 *
 * `confirmUnknown` is the owner answering "yes, I meant that address" to the question the server
 * asked on the previous attempt. It is passed rather than remembered so the answer can only ever
 * apply to the attempt it was given for.
 */
/** One recipient's landed grant, so a multi-recipient run can report each person's own notices. */
export interface GrantOutcome extends ShareNotices {
  identifier: string;
}

/**
 * What a multi-recipient run leaves behind.
 *
 * `failed` is who did NOT land, so the dialog can leave exactly those on screen. `outcomes` is one
 * entry per grant that DID land — the single-attempt state can only hold the last one, and the notice
 * that matters most (a mistyped address that minted a stranger, a link only person #2 needs) is
 * precisely the one an overwrite destroys.
 */
export interface ManyGrantResult {
  failed: string[];
  outcomes: GrantOutcome[];
}

/** What one landed grant leaves the owner to notice. */
export interface ShareNotices {
  /** Nobody answered to that address, so the share created them. Not an error — but the one moment a
   *  typo is observable, since nothing about that share will ever fail afterwards. */
  invited: boolean;
  /** The access is real and the MESSAGE did not go. The owner is the one who has to tell them. */
  notifyFailed: boolean;
  /** Only for a grantee with no Symphony account; somebody who already has one is told in-product. */
  shareLink: string | null;
}

/**
 * The three notices a share result produces, derived in ONE place.
 *
 * `submitShare` renders them for a single attempt and `submitMany` collects them per recipient, and a
 * second derivation is a second chance for the two to disagree about the same result.
 */
export function shareNotices(result: Awaited<ReturnType<ShareFlowDeps['share']>> | undefined): ShareNotices {
  return {
    invited: result?.granteeIsNew === true,
    // ONLY an explicit `false`. Never inferred from the link's absence — a Symphony user is told
    // in-product and gets no link — and never from the field being missing, which means the server
    // did not say. Reading "did not say" as "not told" puts a warning under every share whenever
    // this client is newer than the service behind it.
    // ...and only when this share actually announced something. A repeat share reports
    // `granteeNotified: false` because it deliberately said nothing, not because a send failed.
    notifyFailed: result?.granteeNotified === false && result?.alreadyShared !== true,
    shareLink: result?.shareLink ?? null,
  };
}

export async function submitShare(
  subjectId: string,
  state: ShareDialogState,
  deps: ShareFlowDeps,
  confirmUnknown = false,
): Promise<ShareAttempt> {
  if (state.submitting) return 'failed';
  const email = state.email.trim();
  if (!isEmailShaped(email)) {
    deps.setState({ ...state, error: 'invalidEmail' });
    return 'failed';
  }
  deps.setState({ ...state, submitting: true, error: null, confirmEmail: null, notifyFailed: false, shareLink: null });
  let result: Awaited<ReturnType<ShareFlowDeps['share']>>;
  try {
    result = await deps.share(subjectId, email, confirmUnknown);
  } catch {
    // The field keeps its value: the address is probably right and the network was not, and clearing
    // it makes the reader retype what they already typed.
    deps.setState({ ...state, submitting: false, error: 'shareFailed' });
    return 'failed';
  }
  // NOTHING WAS WRITTEN. Keep the typed address and every choice exactly as they are — the owner is
  // about to answer a question about this attempt, and a cleared form would lose the attempt they
  // are answering about. Emphatically not `initialShareDialogState`.
  if (result?.requiresConfirmation) {
    deps.setState({ ...state, submitting: false, error: null, confirmEmail: email });
    return 'question';
  }
  // The address is remembered ONLY when nobody was behind it, and only until the next action: it is
  // a thing to notice, not a state to sit in.
  const notices = shareNotices(result);
  deps.setState({
    ...initialShareDialogState,
    invitedEmail: notices.invited ? email : null,
    notifyFailed: notices.notifyFailed,
    shareLink: notices.shareLink,
  });
  deps.reload();
  return 'shared';
}

/** The owner declined the question. Nothing was written, so this only clears the question. */
export function cancelConfirm(state: ShareDialogState, deps: ShareFlowDeps): void {
  deps.setState({ ...state, confirmEmail: null });
}

/**
 * Remove pressed on one row. The id passed here is the row's OWN `granteeRecipientId` — the same
 * value the list was rendered from — so the revoke can only ever target the person whose Remove was
 * pressed.
 */
export async function removeShare(
  subjectId: string,
  granteeRecipientId: string,
  state: ShareDialogState,
  deps: ShareFlowDeps,
): Promise<void> {
  if (!granteeRecipientId || state.removing) return;
  deps.setState({ ...state, removing: granteeRecipientId, error: null });
  try {
    await deps.revoke(subjectId, granteeRecipientId);
  } catch {
    deps.setState({ ...state, removing: null, error: 'removeFailed' });
    return;
  }
  deps.setState({ ...state, removing: null, error: null });
  deps.reload();
}
